import { IMAGE_LOADER, ImageLoaderConfig } from '@angular/common';
import { inject, Injectable, Provider } from '@angular/core';
import { IEnvironment, IMediaAndPhotos, IPhotoEntityReference, PhotoSize, PictureDisplayMode, StretchMode } from '../../models';
import { EnvironmentService } from '../environment/environment.service';
import { InformationService } from '../init/information.service';

/**
 * Backend contract — bounding-box pixel size per variant (proportionalbiggest). See
 * Api.Frontend/Models/EntityReference.cs. The small/medium/large slots are pure resize steps of the
 * main picture; the CMS "Auflösungen" feature (a different file per breakpoint) is deliberately not
 * supported by CS4, so no art direction is derived from them.
 */
const BOUNDING_BOX: Record<PhotoSize, number> = {
  [PhotoSize.Small]: 200,
  [PhotoSize.Medium]: 600,
  [PhotoSize.Large]: 1200,
  [PhotoSize.Original]: 2000
};

/** Upper bound of the loaderParams cache; browsing a large catalog would otherwise grow it unbounded. */
const LOADER_PARAMS_CACHE_LIMIT = 500;

/** Always-present sizes, ascending. `Original` is opt-in per photo — see srcsetFor. */
const SIZE_ASCENDING: readonly PhotoSize[] = [PhotoSize.Small, PhotoSize.Medium, PhotoSize.Large];

/**
 * Strips a leading slash from an endpoint path so the loader can cleanly build `${BACKEND_URL}/${path}`.
 * @param value the raw endpoint string
 * @returns the path without a leading slash
 */
const stripLeadingSlash = (value: string): string => (value.startsWith('/') ? value.slice(1) : value);

/**
 * Resolves the base URL for media/file requests: prefers a trimmed FRONTEND_URL, falls back to a
 * trimmed BACKEND_URL, and returns '' when neither is usable (e.g. unset, empty or whitespace-only).
 * @param env the active environment configuration
 * @returns the media base URL, or '' when none is configured
 */
const resolveMediaBaseUrl = (env: IEnvironment): string => env.FRONTEND_URL?.trim() || env.BACKEND_URL?.trim() || '';

@Injectable({
  providedIn: 'root'
})
export class ImageService {
  // Services
  private readonly envService = inject(EnvironmentService);
  private readonly informationService = inject(InformationService);

  // Properties & Signals
  /** Default srcset descriptors across the always-present backend sizes, paired with the IMAGE_LOADER. */
  readonly DEFAULT_SRCSET = SIZE_ASCENDING.map((s) => `${BOUNDING_BOX[s]}w`).join(', ');

  /**
   * srcset descriptors for one photo: the default ladder, plus the 2000w candidate when the photo
   * actually carries an original. The backend emits that variant only where an image runs full
   * width, so advertising 2000w unconditionally would promise a resolution most photos do not have —
   * the loader would then hand out the 1200px url for a candidate the browser believes is 2000px.
   * @param photo the photo entity reference
   * @returns the srcset descriptor list
   */
  srcsetFor(photo: IPhotoEntityReference | undefined): string {
    return photo?.[PhotoSize.Original] ? `${this.DEFAULT_SRCSET}, ${BOUNDING_BOX[PhotoSize.Original]}w` : this.DEFAULT_SRCSET;
  }

  /**
   * Cached per `photoKey` so loaderParams object identity stays stable (avoids NG02953). A re-render
   * delivers the same picture in a fresh object; keying on the url rather than on object identity keeps
   * the `<img>` — which is tracked by the same url — on the loaderParams instance it was created with.
   * String keys are not collected with their photo, so the cache evicts its oldest entries once
   * `LOADER_PARAMS_CACHE_LIMIT` is reached.
   */
  private readonly loaderParamsCache = new Map<string, { photo: IPhotoEntityReference }>();

  // Getters & Setters
  /**
   * Base URL for media/file requests. Prefers FRONTEND_URL so images are served through the frontend
   * origin, falling back to BACKEND_URL. Empty when neither is configured (callers must guard).
   */
  private get mediaBaseUrl(): string {
    return resolveMediaBaseUrl(this.envService.env);
  }

  // Methods
  /**
   * Full URL for a backend-provided media path (photo variant, crop source, noPic). Absolute urls
   * pass through unchanged.
   * @param endpoint the media path as the backend emitted it
   * @returns the full media URL, or undefined when the path or the media base URL is missing
   */
  getMediaUrl(endpoint: string | undefined): string | undefined {
    if (!endpoint) return undefined;
    if (endpoint.startsWith('http')) return endpoint;
    if (!this.mediaBaseUrl) return undefined;
    return this.mediaBaseUrl + '/' + stripLeadingSlash(endpoint);
  }

  /**
   * Returns the full noPic fallback image URL (prefixed with the media base URL).
   * @param size the desired photo size
   * @returns the noPic image URL, or undefined if not configured
   */
  getNoPicUrl(size: `${PhotoSize}` = PhotoSize.Medium): string | undefined {
    return this.getMediaUrl(this.getNoPicEndpoint(size));
  }

  /**
   * Endpoint path for use with ngSrc; the IMAGE_LOADER prepends the media base URL. Falls back to noPic.
   * @param photo the photo entity reference
   * @param size the desired photo size
   * @returns the endpoint path (without media base URL), or undefined
   */
  getEndpoint(photo: IPhotoEntityReference | undefined, size: `${PhotoSize}` = PhotoSize.Medium): string | undefined {
    if (!photo) return this.getNoPicEndpoint(size);
    // `original` is only set for full-width images, so step down to large (1200) rather than
    // all the way to medium (600).
    const fallback = size === PhotoSize.Original ? photo[PhotoSize.Large] : undefined;
    const sizeValue = photo[size] || fallback || photo[PhotoSize.Medium];
    if (!sizeValue) return this.getNoPicEndpoint(size);
    return stripLeadingSlash(sizeValue);
  }

  /**
   * Full URL (with media base URL prefix) for places where ngSrc/IMAGE_LOADER is not available (e.g. SVG `<image>`).
   * @param photo the photo entity reference
   * @param size the desired photo size
   * @returns the full image URL, or undefined
   */
  getPhotoUrl(photo: IPhotoEntityReference | undefined, size: `${PhotoSize}` = PhotoSize.Large): string | undefined {
    return this.getMediaUrl(this.getEndpoint(photo, size));
  }

  /**
   * Full media URL for a file id, using the `/files` query endpoint. Handy when only the file id is
   * known (e.g. an initially-selected picture whose photo reference is not resolved yet).
   * @param fileId the backend file id
   * @param size the desired photo size (bounding box)
   * @returns the full file URL, or undefined when no media base URL is configured
   */
  getFileUrlById(fileId: number | string | undefined, size: `${PhotoSize}` = PhotoSize.Original): string | undefined {
    if (fileId == null || !this.mediaBaseUrl) return undefined;
    const boundingBox = BOUNDING_BOX[size] ?? BOUNDING_BOX[PhotoSize.Medium];
    const id = encodeURIComponent(fileId);
    return `${this.mediaBaseUrl}/files?id=${id}&width=${boundingBox}&height=${boundingBox}`;
  }

  /**
   * Endpoint path for the noPic fallback (without media base URL).
   * @param size the desired photo size
   * @returns the noPic endpoint path, or undefined
   */
  getNoPicEndpoint(size: `${PhotoSize}` = PhotoSize.Medium): string | undefined {
    const noPic = this.informationService.init()?.noPic;
    if (!noPic) return undefined;
    const sizeValue = noPic[size] || noPic[PhotoSize.Medium];
    if (!sizeValue) return undefined;
    return stripLeadingSlash(sizeValue);
  }

  /**
   * Like getEndpoint but accepts an IMediaAndPhotos item and extracts the photo from it.
   * @param item the media-and-photos item or a direct photo entity reference
   * @param size the desired photo size
   * @param noPicFallback whether to return the noPic endpoint when no photo is found
   * @returns the endpoint path (without BACKEND_URL), or undefined
   */
  getPhotoEndpoint(
    item: IMediaAndPhotos | IPhotoEntityReference | undefined,
    size: `${PhotoSize}` = PhotoSize.Medium,
    noPicFallback: boolean = false
  ): string | undefined {
    if (!item) return noPicFallback ? this.getNoPicEndpoint(size) : undefined;
    if ('media' in item && item.media?.video?.picture) {
      return this.getEndpoint(item.media.video.picture, size);
    } else if ('photo' in item && item.photo) {
      return this.getEndpoint(item.photo, size);
    } else if ('medium' in item) {
      // Not `'original' in item`: api/f/ serialises with NullValueHandling.Ignore, so an unset
      // `original` is absent from the JSON. `medium` is always present and thus the reliable marker.
      return this.getEndpoint(item as IPhotoEntityReference, size);
    }
    return noPicFallback ? this.getNoPicEndpoint(size) : undefined;
  }

  /**
   * Maps backend StretchMode → Tailwind object-fit/position classes. The override wins over the photo's StretchMode unless it is Auto.
   * @param stretchMode the stretch mode from the photo entity
   * @param override optional display mode override from component settings
   * @returns space-separated CSS classes (e.g. 'object-cover', 'object-contain object-top')
   */
  getObjectFitFromStretchMode(stretchMode?: StretchMode, override?: PictureDisplayMode): string {
    if (override !== undefined && override !== PictureDisplayMode.Auto) {
      switch (override) {
        case PictureDisplayMode.Cut:
          return 'object-cover';
        case PictureDisplayMode.Full:
          return 'object-contain';
      }
    }

    switch (stretchMode) {
      case StretchMode.Exact:
        return 'object-fill';
      case StretchMode.Resize:
      case StretchMode.Stage:
      case StretchMode.ProportionalSmallest:
      case StretchMode.Auto:
        return 'object-cover';
      case StretchMode.ProportionalBiggest:
      case StretchMode.ProportionalExact:
        return 'object-contain';
      case StretchMode.ProportionalExactTop:
        return 'object-contain object-top';
      case StretchMode.ProportionalExactBottom:
        return 'object-contain object-bottom';
      case StretchMode.ProportionalExactLeft:
        return 'object-contain object-left';
      case StretchMode.Original:
        return 'object-none';
      default:
        return 'object-cover';
    }
  }

  /**
   * Intrinsic pixel dimensions of a photo variant. Longer edge = bounding box; the other side scales via widthFactor/heightFactor.
   * @param photo the photo entity reference
   * @param size the chosen backend variant
   * @returns pixel width and height, both rounded; a square bounding box when no photo is given
   */
  getIntrinsicDimensions(
    photo: IPhotoEntityReference | undefined,
    size: `${PhotoSize}` = PhotoSize.Medium
  ): { width: number; height: number } {
    // Guarded like getFileUrlById: an unknown size would make boundingBox undefined and turn the
    // returned width/height into NaN, which NgOptimizedImage then renders as broken dimensions.
    const boundingBox = BOUNDING_BOX[size] ?? BOUNDING_BOX[PhotoSize.Medium];
    if (!photo) return { width: boundingBox, height: boundingBox };
    const widthFactor = photo.widthFactor || 1;
    const heightFactor = photo.heightFactor || 1;
    const max = Math.max(widthFactor, heightFactor) || 1;
    return {
      width: Math.round((boundingBox * widthFactor) / max),
      height: Math.round((boundingBox * heightFactor) / max)
    };
  }

  /**
   * Convenience wrapper around getIntrinsicDimensions.
   * @param photo the photo entity reference
   * @param size the chosen backend variant
   * @returns intrinsic pixel width, rounded
   */
  getIntrinsicWidth(photo: IPhotoEntityReference | undefined, size: `${PhotoSize}` = PhotoSize.Medium): number {
    return this.getIntrinsicDimensions(photo, size).width;
  }

  /**
   * Convenience wrapper around getIntrinsicDimensions.
   * @param photo the photo entity reference
   * @param size the chosen backend variant
   * @returns intrinsic pixel height, rounded
   */
  getIntrinsicHeight(photo: IPhotoEntityReference | undefined, size: `${PhotoSize}` = PhotoSize.Medium): number {
    return this.getIntrinsicDimensions(photo, size).height;
  }

  /**
   * Aspect ratio (width/height) from the backend factors. Use as CSS `aspect-ratio` on adaptive wrappers.
   * @param photo the photo entity reference
   * @returns widthFactor / heightFactor, or 1 when factors are missing
   */
  getIntrinsicAspectRatio(photo: IPhotoEntityReference | undefined): number {
    if (!photo) return 1;
    return (photo.widthFactor || 1) / (photo.heightFactor || 1);
  }

  /**
   * Stable identity of a photo across re-renders: its first available variant url. A re-render delivers
   * the same picture in a fresh object, so url identity — not object identity — is what keeps an `<img>`
   * and its loaderParams together. The templates track their `<img>` by `medium` alone; the fallback
   * chain here only keeps the cache usable for photos whose `medium` is empty, where every such photo
   * shares one key on both sides.
   * @param photo the photo entity reference
   * @returns the identifying url, or 'nopic' when the photo is absent or carries no variant
   */
  photoKey(photo: IPhotoEntityReference | undefined): string {
    if (!photo) return 'nopic';
    return (
      photo[PhotoSize.Medium] || photo[PhotoSize.Large] || photo[PhotoSize.Small] || photo[PhotoSize.Original] || 'nopic'
    );
  }

  /**
   * loaderParams for NgOptimizedImage. Cached by `photoKey` so object identity stays stable across
   * renders (NG02953).
   * @param photo the photo entity reference
   * @returns the loaderParams object, or undefined when no photo is given
   */
  getLoaderParams(photo: IPhotoEntityReference | undefined): { photo: IPhotoEntityReference } | undefined {
    if (!photo) return undefined;
    const key = this.photoKey(photo);
    const cached = this.loaderParamsCache.get(key);
    if (cached) {
      // The backend emits `original` only for full-width images, so the same file can arrive both with
      // and without it. Adopting the richer object keeps the loader in sync with `srcsetFor`, which
      // would otherwise advertise a 2000w candidate the cached photo cannot serve.
      if (photo[PhotoSize.Original] && !cached.photo[PhotoSize.Original]) cached.photo = photo;
      return cached;
    }
    if (this.loaderParamsCache.size >= LOADER_PARAMS_CACHE_LIMIT) {
      const oldest = this.loaderParamsCache.keys().next();
      if (!oldest.done) this.loaderParamsCache.delete(oldest.value);
    }
    const params = { photo };
    this.loaderParamsCache.set(key, params);
    return params;
  }
}

/**
 * IMAGE_LOADER provider for NgOptimizedImage: prepends the media base URL (FRONTEND_URL, falling back
 * to BACKEND_URL) and picks the smallest matching backend variant from `loaderParams.photo` based on
 * the requested width. Register alongside `provideHttpClient`, `provideRouter`, etc.
 * @returns Angular provider configuration for IMAGE_LOADER
 */
export function provideImageLoader(): Provider {
  return {
    provide: IMAGE_LOADER,
    useFactory: () => {
      const envService = inject(EnvironmentService);
      const prepend = (url: string): string => {
        if (url.startsWith('http')) return url;
        const baseUrl = resolveMediaBaseUrl(envService.env);
        if (!baseUrl) return url;
        return `${baseUrl}/${stripLeadingSlash(url)}`;
      };
      return (config: ImageLoaderConfig) => {
        const photo = config.loaderParams?.['photo'] as IPhotoEntityReference | undefined;
        if (photo && config.width) {
          for (const size of SIZE_ASCENDING) {
            if (config.width <= BOUNDING_BOX[size] && photo[size]) return prepend(photo[size]);
          }
          if (photo.original) return prepend(photo.original);
        }
        return prepend(config.src);
      };
    }
  };
}
